import { Link } from "react-router-dom";
import {
  Calculator,
  TrendingDown,
  ArrowLeftRight,
  ArrowRight,
  BookOpen,
  Sparkles,
} from "lucide-react";
import SEO from "../ui/SEO.jsx";
import CurrencyWidget from "../ui/CurrencyWidget.jsx";
import AdSlot from "../ui/AdSlot.jsx";
import { AD_SLOTS } from "../lib/adsense.js";

export default function Home() {
  return (
    <div>
      <SEO />
      
      {/* Hero */}
      <section className="mb-20">
        <div className="text-[11px] tracking-[0.18em] uppercase text-accent mb-5 font-semibold">
          Para brasileiros no Japão
        </div>
        <h1 className="font-serif font-medium text-[44px] md:text-[64px] leading-[1.02] tracking-tight mb-6">
          Decisões{" "}
          <span className="italic text-accent">honestas</span>{" "}
          sobre dinheiro,<br />
          impostos e o futuro.
        </h1>
        <p className="text-lg md:text-xl text-ink-soft leading-relaxed max-w-xl mb-8">
          Calculadoras e guias práticos feitos por quem vive aqui. Sem
          marketing de remessadora, sem promessa fácil, sem &ldquo;volta logo&rdquo;.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md">
          <Link
            to="/calculadoras/salario"
            className="flex items-center justify-center gap-2 bg-ink text-bg py-4 px-6 rounded-xl font-semibold no-underline hover:bg-ink/90 transition-colors"
          >
            Calcular meu salário líquido
            <ArrowRight size={18} />
          </Link>
          <Link
            to="/guias"
            className="flex items-center justify-center gap-2 bg-transparent text-ink py-4 px-6 rounded-xl font-semibold border border-border no-underline hover:bg-surface transition-colors"
          >
            Ler os guias
          </Link>
        </div>
      </section>

      {/* Live FX rates — gives recurring visitors a reason to return */}
      <CurrencyWidget />

      {/* Calculators */}
      <section className="mb-20">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif font-medium text-3xl tracking-tight">
            Calculadoras
          </h2>
          <Link to="/calculadoras" className="text-sm text-accent no-underline hover:underline">
            Ver todas →
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <CalcCard
            to="/calculadoras/salario"
            icon={<Calculator size={20} />}
            title="Salário líquido"
            description="Quanto você realmente recebe depois do shakai hoken, imposto de renda e juushinkoku."
            tag="Mais usada"
          />
          <CalcCard
            to="/calculadoras/aposentadoria"
            icon={<TrendingDown size={20} />}
            title="Dattai ichijikin"
            description="Quanto você pegaria de volta da aposentadoria se voltasse para o Brasil."
          />
          <CalcCard
            to="/calculadoras/remessas"
            icon={<ArrowLeftRight size={20} />}
            title="Comparar remessas"
            description="Wise, Brastel, GoRemit — quem realmente entrega mais reais por iene hoje."
          />
          <ComingSoonCard
            title="Kakutei shinkoku"
            description="Calculadora do ajuste anual de imposto de renda. Em breve."
          />
        </div>
      </section>

      <AdSlot slot={AD_SLOTS.homeMid} format="auto" />

      {/* Guides */}
      <section className="mb-20">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-serif font-medium text-3xl tracking-tight">
            Guias
          </h2>
          <Link to="/guias" className="text-sm text-accent no-underline hover:underline">
            Ver todos →
          </Link>
        </div>
        <div className="space-y-3">
          <GuideRow
            to="/guias/vale-a-pena-voltar"
            title="Vale a pena voltar para o Brasil?"
            description="A pergunta que ninguém responde direito. Vamos fazer as contas honestas."
          />
          <GuideRow
            to="/guias/kakutei-shinkoku"
            title="Como fazer kakutei shinkoku"
            description="O ajuste anual de imposto explicado em português, sem termos confusos."
          />
          <GuideRow
            to="/guias/gaimen-kirikae"
            title="Carteira de motorista no Japão"
            description="Conversão da habilitação brasileira (gaimen kirikae) passo a passo."
          />
          <GuideRow
            to="/guias/shakai-vs-kokumin"
            title="Shakai hoken vs kokumin hoken"
            description="Qual seguro escolher quando você muda de emprego ou vira autônomo."
          />
          <GuideRow
            to="/guias/mandar-dinheiro-para-o-brasil"
            title="Como mandar dinheiro para o Brasil"
            description="Comparativo honesto entre Wise, Brastel, GoRemit e outras opções."
          />
        </div>
      </section>

      {/* Trust */}
      <section className="border-t border-border pt-10 text-center">
        <div className="font-serif italic text-xl text-ink-soft mb-2">
          &ldquo;Honestidade antes de otimismo.&rdquo;
        </div>
        <p className="text-sm text-ink-soft max-w-md mx-auto leading-relaxed">
          Este site é feito por um brasileiro que mora no Japão e faz as mesmas
          contas no fim do mês. Não vendemos visto, não somos despachante, não
          ganhamos comissão por opinião.
        </p>
        <Link
          to="/sobre"
          className="inline-block mt-4 text-accent text-sm no-underline hover:underline"
        >
          Saiba mais sobre o projeto →
        </Link>
      </section>
    </div>
  );
}

function CalcCard({ to, icon, title, description, tag }) {
  return (
    <Link
      to={to}
      className="block border border-border rounded-2xl p-6 bg-surface hover:border-accent transition-colors no-underline text-ink group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-accent-soft flex items-center justify-center text-accent">
          {icon}
        </div>
        {tag && (
          <span className="text-[10px] tracking-wider uppercase font-mono text-accent bg-accent-soft px-2 py-1 rounded">
            {tag}
          </span>
        )}
      </div>
      <h3 className="font-serif text-xl font-medium mb-2 group-hover:text-accent transition-colors">
        {title}
      </h3>
      <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
    </Link>
  );
}

function ComingSoonCard({ title, description }) {
  return (
    <div className="border border-dashed border-border rounded-2xl p-6 opacity-60">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-border flex items-center justify-center text-ink-soft">
          <Sparkles size={20} />
        </div>
        <span className="text-[10px] tracking-wider uppercase font-mono text-ink-soft bg-border/50 px-2 py-1 rounded">
          Em breve
        </span>
      </div>
      <h3 className="font-serif text-xl font-medium mb-2">{title}</h3>
      <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
    </div>
  );
}

function GuideRow({ to, title, description }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-4 p-5 rounded-xl border border-border bg-surface hover:border-accent transition-colors no-underline text-ink group"
    >
      <div className="flex-1">
        <h3 className="font-serif text-lg md:text-xl font-medium mb-1 group-hover:text-accent transition-colors">
          {title}
        </h3>
        <p className="text-sm text-ink-soft leading-relaxed">{description}</p>
      </div>
      <ArrowRight size={20} className="text-ink-soft shrink-0 group-hover:text-accent transition-colors" />
    </Link>
  );
}
